import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Code,
    Collapse,
    Divider,
    IconButton,
    Input,
    List,
    ListItem,
    Spinner,
    VStack,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { getAuth, signOut } from 'firebase/auth';
import { collection, doc, getDoc, onSnapshot, orderBy, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { db } from './firebase';
import './LogList.css';
import PortalHeader from './PortalHeader';

function LegacyLogList() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [duration, setDuration] = useState('1d');
    const [expandedGroups, setExpandedGroups] = useState([]);
    const [expandedLogIds, setExpandedLogIds] = useState([]);
    const [hideResolved, setHideResolved] = useState(false);
    const [errorFilters, setErrorFilters] = useState([]);
    const [activeErrorFilters, setActiveErrorFilters] = useState([]);
    const [newError, setNewError] = useState('');
    const [showErrorFilters, setShowErrorFilters] = useState(false);
    const [platformFilter, setPlatformFilter] = useState('All');

    const FILTER_DOC_ID = '(filter)';

    const getStartDate = (selectedDuration) => {
        const start = new Date();
        start.setHours(0, 0, 0, 0);

        switch (selectedDuration) {
            case '1d':
                break;
            case '3d':
                start.setDate(start.getDate() - 3);
                break;
            case '7d':
                start.setDate(start.getDate() - 7);
                break;
            case '15d':
                start.setDate(start.getDate() - 15);
                break;
            case '1m':
                start.setMonth(start.getMonth() - 1);
                break;
            case '3m':
                start.setMonth(start.getMonth() - 3);
                break;
            default:
                break;
        }

        return start;
    };

    const fetchErrorFilters = async () => {
        const ref = doc(db, 'Logs', FILTER_DOC_ID);
        const snap = await getDoc(ref);

        if (snap.exists()) {
            const data = snap.data();
            setErrorFilters(data.errors || []);
        }
    };

    const saveErrorFilters = async (errors) => {
        await setDoc(doc(db, 'Logs', FILTER_DOC_ID), { errors });
    };

    const toggleErrorFilter = (error) => {
        setActiveErrorFilters((prev) =>
            prev.includes(error)
                ? prev.filter((entry) => entry !== error)
                : [...prev, error]
        );
    };

    const addNewErrorFilter = async () => {
        const updated = [...new Set([...errorFilters, newError.trim()])].filter(Boolean);
        await saveErrorFilters(updated);
        setErrorFilters(updated);
        setActiveErrorFilters(updated);
        setNewError('');
    };

    useEffect(() => {
        fetchErrorFilters();
    }, []);

    useEffect(() => {
        if (!duration) {
            return undefined;
        }

        setLoading(true);

        const startTime = getStartDate(duration);
        const logsRef = collection(db, 'Logs');
        const logsQuery = query(logsRef, where('createdTime', '>=', startTime), orderBy('createdTime', 'desc'));

        const unsubscribe = onSnapshot(logsQuery, (snapshot) => {
            let nextLogs = snapshot.docs.map((logDoc) => ({ id: logDoc.id, ...logDoc.data() }));

            if (hideResolved) {
                nextLogs = nextLogs.filter((log) => !log.resolved);
            }

            if (activeErrorFilters.length > 0) {
                nextLogs = nextLogs.filter((log) => {
                    const errorMessage = String(log.data?.error || '').toLowerCase();

                    return !activeErrorFilters.some((filter) =>
                        errorMessage.includes(String(filter || '').toLowerCase())
                    );
                });
            }

            if (platformFilter !== 'All') {
                const normalizedPlatformFilter = platformFilter.toLowerCase();
                nextLogs = nextLogs.filter((log) =>
                    String(log.platform || '').toLowerCase().includes(normalizedPlatformFilter)
                );
            }

            setLogs(nextLogs);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [duration, hideResolved, activeErrorFilters, platformFilter]);

    const groupedLogs = useMemo(() => {
        const groups = {};

        for (const log of logs) {
            const title = `${log.event} on ${log.appName} (v${log.appVersion || 'Unknown'})`;
            if (!groups[title]) {
                groups[title] = [];
            }
            groups[title].push(log);
        }

        return Object.entries(groups);
    }, [logs]);

    const toggleGroup = (title) => {
        setExpandedGroups((prev) =>
            prev.includes(title) ? prev.filter((entry) => entry !== title) : [...prev, title]
        );
    };

    const toggleExpand = (id) => {
        setExpandedLogIds((prev) =>
            prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]
        );
    };

    const markAsResolved = async (logId, value) => {
        const logRef = doc(db, 'Logs', logId);
        await updateDoc(logRef, { resolved: value });
        setLogs((prev) => prev.map((log) => (log.id === logId ? { ...log, resolved: value } : log)));
    };

    const handleLogout = () => {
        const auth = getAuth();
        signOut(auth)
            .then(() => {
                window.location.href = '/login';
            })
            .catch((error) => {
                console.error('Error signing out: ', error);
            });
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp?.seconds) {
            return 'Unknown time';
        }

        return new Date(timestamp.seconds * 1000).toLocaleString();
    };

    const durationOptions = [
        { label: 'Today', value: '1d' },
        { label: 'Past 3 days', value: '3d' },
        { label: 'Past 7 days', value: '7d' },
        { label: 'Past 15 days', value: '15d' },
        { label: 'Past month', value: '1m' },
        { label: 'Past 3 months', value: '3m' },
    ];

    const platformOptions = ['All', 'iOS', 'Android'];

    return (
        <div className="logs-page">
            <PortalHeader>
                    <button onClick={handleLogout} className="logs-logout-button">Sign out</button>
            </PortalHeader>

            <main className="logs-main">
                <section className="logs-hero-card">
                    <div>
                        <p className="logs-eyebrow">Internal portal</p>
                        <h1 className="logs-title">Legacy logs</h1>
                        <p className="logs-description">
                            Investigate failures and manage resolved issues across FieldOps builds.
                        </p>
                    </div>
                    <div className="logs-summary-grid">
                        <div className="logs-summary-item">
                            <span className="logs-summary-label">Visible logs</span>
                            <strong>{logs.length}</strong>
                        </div>
                        <div className="logs-summary-item">
                            <span className="logs-summary-label">Groups</span>
                            <strong>{groupedLogs.length}</strong>
                        </div>
                        <div className="logs-summary-item">
                            <span className="logs-summary-label">Range</span>
                            <strong>{durationOptions.find((option) => option.value === duration)?.label}</strong>
                        </div>
                    </div>
                </section>

                <section className="logs-filter-card">
                    <div className="logs-filter-section">
                        <span className="logs-filter-label">Date range</span>
                        <div className="logs-chip-row">
                            {durationOptions.map(({ label, value }) => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`logs-chip ${duration === value ? 'active' : ''}`}
                                    onClick={() => {
                                        setLogs([]);
                                        setDuration(value);
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="logs-filter-section">
                        <span className="logs-filter-label">Platform</span>
                        <div className="logs-chip-row">
                            {platformOptions.map((platform) => (
                                <button
                                    key={platform}
                                    type="button"
                                    className={`logs-chip ${platformFilter === platform ? 'active' : ''}`}
                                    onClick={() => setPlatformFilter(platform)}
                                >
                                    {platform}
                                </button>
                            ))}
                        </div>
                    </div>

                    <label className="logs-toggle">
                        <Checkbox
                            isChecked={hideResolved}
                            onChange={(event) => setHideResolved(event.target.checked)}
                            colorScheme="orange"
                        >
                            Hide resolved logs
                        </Checkbox>
                    </label>
                </section>

                <section className="logs-filter-card">
                    <div className="logs-filter-header">
                        <div>
                            <h2 className="logs-panel-title">Error filters</h2>
                            <p className="logs-panel-caption">Hide recurring error messages from the list below.</p>
                        </div>
                        <div className="logs-filter-actions">
                            {errorFilters.length > 0 && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        const allChecked = activeErrorFilters.length === errorFilters.length;
                                        setActiveErrorFilters(allChecked ? [] : [...errorFilters]);
                                    }}
                                >
                                    {activeErrorFilters.length === errorFilters.length ? 'Clear selection' : 'Select all'}
                                </Button>
                            )}
                            <IconButton
                                icon={showErrorFilters ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                onClick={() => setShowErrorFilters(!showErrorFilters)}
                                size="sm"
                                variant="ghost"
                                aria-label="Toggle error filter panel"
                            />
                        </div>
                    </div>

                    <Collapse in={showErrorFilters} animateOpacity>
                        <Box mt={4}>
                            <div className="logs-error-add">
                                <Input
                                    placeholder="Add an error message to filter out"
                                    size="sm"
                                    value={newError}
                                    onChange={(event) => setNewError(event.target.value)}
                                />
                                <Button size="sm" colorScheme="orange" onClick={addNewErrorFilter}>
                                    Add
                                </Button>
                            </div>
                            <List mt={3} spacing={2}>
                                {errorFilters.map((err, index) => (
                                    <ListItem key={index}>
                                        <Checkbox
                                            size="sm"
                                            isChecked={activeErrorFilters.includes(err)}
                                            onChange={() => toggleErrorFilter(err)}
                                            colorScheme="orange"
                                        >
                                            {err}
                                        </Checkbox>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Collapse>
                </section>

                <section className="logs-groups">
                    {loading ? (
                        <div className="logs-loading">
                            <Spinner size="lg" color="orange.400" />
                        </div>
                    ) : null}

                    {!loading && groupedLogs.length === 0 ? (
                        <div className="logs-empty-state">
                            <h2>No logs found</h2>
                            <p>Try a broader date range or clear one of the active filters.</p>
                        </div>
                    ) : null}

                    {!loading && groupedLogs.map(([title, groupEntries]) => {
                        const isExpanded = expandedGroups.includes(title);
                        const resolvedCount = groupEntries.filter((entry) => entry.resolved).length;

                        return (
                            <article key={title} className="log-group-card">
                                <button
                                    type="button"
                                    className="log-group-toggle"
                                    onClick={() => toggleGroup(title)}
                                >
                                    <div className="log-group-copy">
                                        <h2 className="log-group-title">{title}</h2>
                                        <div className="log-group-meta">
                                            <span>{groupEntries.length} entries</span>
                                            <span>{resolvedCount} resolved</span>
                                        </div>
                                    </div>
                                    <span className="log-group-icon" aria-hidden="true">
                                        {isExpanded ? '−' : '+'}
                                    </span>
                                </button>

                                <Collapse in={isExpanded} animateOpacity>
                                    <VStack spacing={3} mt={4} align="stretch">
                                        {groupEntries.map((log) => {
                                            const logExpanded = expandedLogIds.includes(log.id);

                                            return (
                                                <Box key={log.id} className="log-entry-card">
                                                    <div className="log-entry-top">
                                                        <div className="log-entry-summary">
                                                            <div className="log-entry-badges">
                                                                <span className={`log-pill ${log.resolved ? 'resolved' : 'open'}`}>
                                                                    {log.resolved ? 'Resolved' : 'Open'}
                                                                </span>
                                                                <span className="log-pill neutral">{log.platform || 'Unknown platform'}</span>
                                                            </div>
                                                            <p className="log-entry-time">{formatTimestamp(log.createdTime)}</p>
                                                        </div>

                                                        <div className="log-entry-actions">
                                                            <Checkbox
                                                                size="sm"
                                                                isChecked={log.resolved || false}
                                                                onChange={(event) => markAsResolved(log.id, event.target.checked)}
                                                                colorScheme="orange"
                                                            >
                                                                Resolved
                                                            </Checkbox>
                                                            <IconButton
                                                                icon={logExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                                                onClick={() => toggleExpand(log.id)}
                                                                size="xs"
                                                                variant="ghost"
                                                                aria-label="Toggle log payload"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="log-entry-grid">
                                                        <div>
                                                            <span className="log-entry-label">App</span>
                                                            <p className="log-entry-value">
                                                                {log.appName || 'Unknown'} ({log.appId || 'Unknown'})
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="log-entry-label">User</span>
                                                            <p className="log-entry-value">
                                                                {log.userName || 'Unknown'} ({log.userId || 'Unknown'})
                                                            </p>
                                                        </div>
                                                        <div className="log-entry-grid-full">
                                                            <span className="log-entry-label">Log ID</span>
                                                            <p className="log-entry-value subtle">{log.id}</p>
                                                        </div>
                                                        <div className="log-entry-grid-full">
                                                            <span className="log-entry-label">Error</span>
                                                            <p className="log-entry-error">{log.data?.error || 'None'}</p>
                                                        </div>
                                                    </div>

                                                    <Collapse in={logExpanded} animateOpacity>
                                                        <Divider my={4} />
                                                        <Code whiteSpace="pre-wrap" display="block" className="log-payload">
                                                            {JSON.stringify(log.data, null, 2)}
                                                        </Code>
                                                    </Collapse>
                                                </Box>
                                            );
                                        })}
                                    </VStack>
                                </Collapse>
                            </article>
                        );
                    })}
                </section>
            </main>
        </div>
    );
}

export default LegacyLogList;
